import { api } from "@/features/utils/fetch-server";
import type {
  AttachedUser,
  Client,
  ClientBranding,
  ClientCallRecording,
  ClientDeletionEligibility,
  ClientInvitation,
  ClientMembership,
  ClientMembershipStatus,
  ClientAccessWithUser,
  ClientMemberWithUser,
  ClientRole,
  ClientWithMembership,
  TeamMember,
} from "../types";

export class ClientsAPI {
  // Get all clients for current user
  static getMyClients = async (): Promise<ClientWithMembership[]> => {
    const data = await api.get<{ clients?: ClientWithMembership[] }>(
      "/api/users/v1/clients",
    );
    return data.clients || [];
  };

  // Get pending invitations
  static getPendingInvitations = async (): Promise<ClientInvitation[]> => {
    const data = await api.get<{ invitations?: ClientInvitation[] }>(
      "/api/users/v1/clients/invitations",
    );
    return data.invitations || [];
  };

  // Create a new client
  static createClient = async (data: {
    name: string;
  }): Promise<ClientWithMembership> =>
    api.post("/api/users/v1/clients/create", data);

  // Update client
  static updateClient = async (
    clientId: string,
    data: {
      name?: string;
      logo?: string;
      website?: string;
      about?: string;
      address?: string;
      branding?: ClientBranding;
      call_recording?: ClientCallRecording;
    },
  ): Promise<{ client: Client }> =>
    api.put(`/api/users/v1/clients/${clientId}`, data);

  // Check whether the current owner may delete a client, and what blocks it
  static getDeletionEligibility = async (
    clientId: string,
  ): Promise<ClientDeletionEligibility> =>
    api.get(`/api/users/v1/clients/${clientId}/deletion-eligibility`);

  // Delete client
  static deleteClient = async (
    clientId: string,
  ): Promise<{ success: boolean }> =>
    api.delete(`/api/users/v1/clients/${clientId}`);

  /**
   * The team and the accesses, in one call — they are shown side by side and
   * the attachment between them is what makes either legible.
   *
   * The server sends members as `{ member, user, access }`; the team list is
   * reshaped here into `{ user, membership }` because every picker and calendar
   * view already reads `entry.membership.display_name`.
   */
  static getClientTeam = async (
    clientId: string,
  ): Promise<{
    members: ClientMemberWithUser[];
    accesses: ClientAccessWithUser[];
  }> => {
    const data = await api.get<{
      members?: { member: TeamMember; user: AttachedUser | null }[];
      accesses?: ClientAccessWithUser[];
    }>(`/api/users/v1/clients/${clientId}/team`);
    return {
      members: (data.members || []).map(
        (row: { member: TeamMember; user: AttachedUser | null }) => ({
          membership: row.member,
          user: row.user,
          isPending: false,
        }),
      ),
      accesses: data.accesses || [],
    };
  };

  /** Add somebody to the team. A name is all it takes — no invitation. */
  static createMember = async (
    clientId: string,
    data: { name: string; role_text?: string; phone?: string },
  ): Promise<{ member: TeamMember }> =>
    api.post(`/api/users/v1/clients/${clientId}/members`, data);

  static removeMember = async (
    clientId: string,
    memberId: string,
  ): Promise<{ success: boolean }> =>
    api.delete(`/api/users/v1/clients/${clientId}/members/${memberId}`);

  /** Say who an access is on the team. An empty `memberId` detaches it. */
  static setAccessMember = async (
    clientId: string,
    accessId: string,
    memberId: string,
  ): Promise<{ success: boolean }> =>
    api.put(`/api/users/v1/clients/${clientId}/accesses/${accessId}/member`, {
      member_id: memberId,
    });

  /** Invite somebody: an access of this tenant, pending until they accept. */
  static inviteUser = async (
    clientId: string,
    data: { email: string; role?: ClientRole },
  ): Promise<{ invitation: ClientMembershipStatus }> =>
    api.post(`/api/users/v1/clients/${clientId}/accesses`, data);

  // Accept invitation
  static acceptInvitation = async (
    invitationId: string,
  ): Promise<{ membership: ClientMembership }> =>
    api.post(`/api/users/v1/clients/invitations/${invitationId}/accept`);

  /**
   * Take an access away, whatever state it is in: an accepted login is
   * revoked, an invitation nobody answered is cancelled. One row, one call —
   * cancelling used to be its own endpoint on its own path.
   *
   * Addressed by the access id (`access.access.id`), never by the account
   * behind it: a pending invitation has no account, and an account id sent to
   * a path that reads member ids is how a revocation came to silently remove
   * nothing.
   */
  static removeAccess = async (
    clientId: string,
    accessId: string,
  ): Promise<{ success: boolean }> =>
    api.delete(`/api/users/v1/clients/${clientId}/accesses/${accessId}`);

  /** Give up your own access to a client. Needs no permission — see the route. */
  static leaveClient = async (
    clientId: string,
  ): Promise<{ success: boolean }> =>
    api.post(`/api/users/v1/clients/${clientId}/leave`);

  /** Change what an access may do here. */
  static updateAccessRole = async (
    clientId: string,
    accessId: string,
    role: ClientRole,
  ): Promise<{ membership: ClientMembershipStatus }> =>
    api.put(`/api/users/v1/clients/${clientId}/accesses/${accessId}/role`, {
      role,
    });

  /** Everything the agent knows about a team member. Keyed by member id. */
  static updateMemberProfile = async (
    clientId: string,
    memberId: string,
    updates: {
      display_name?: string;
      schedule_id?: string | null;
      calendars_ids?: { ids: string[] };
      phone?: string;
      device_id?: string;
      sip_extension?: string;
      accept_call_transfers?: boolean;
      role_text?: string;
      hidden_from_agents?: boolean;
    },
  ): Promise<{ membership: ClientMembershipStatus }> =>
    api.put(
      `/api/users/v1/clients/${clientId}/members/${memberId}/profile`,
      updates,
    );
}
